import { type ChangeEvent, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { Modal, StatusIndicator } from "@unified-checkout/design-system";
import Rating from "libs/design-system/src/components/Rating";
import TextArea from "libs/design-system/src/components/TextArea";
import { Check, CommentsQuestionCheck } from "@unified-checkout/design-system/icons/index";

import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { useLangStore } from "@app/dashboard/store/lang";
import type { IFeedbackModal } from "@app/dashboard/types/innerLayout/submit-feedback";
import { submitFeedback } from "@app/dashboard/services/inner_layout/submit-feedback";
import styles from "./submit-feedback.module.scss";

// const SUCCESS_TIMEOUT = 3000;

export const SubmitFeedback = ({ feedbackModal }: { feedbackModal: IFeedbackModal }) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);

  const isWhiteSpacesOnly = feedback.trim().length === 0;

  const {
    isOpen,
    setIsOpen,
  } = feedbackModal;

  const {
    isPending,
    mutate: mutateFeedback,
    isSuccess: isFeedbackSuccess,
  } = useMutation({
    mutationKey: [QUERY_KEYS.SUBMIT_FEEDBACK],
    mutationFn: () => submitFeedback({ rating: rating, feedback: feedback.trim() }),
    onSuccess: () => {
      setShowSuccessMsg(true);
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const msg = response?.data?.message;

      setError(msg);
    }
  });

  const resetToDefault = () => {
    setFeedback("");
    setRating(0);
    setError("");
  };

  useEffect(() => {
    if (!isOpen && isFeedbackSuccess) {
      resetToDefault();
      setShowSuccessMsg(false);
    }
  }, [isOpen, isFeedbackSuccess]);

  const onChangeHandler = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = (e.target.value);
    setFeedback(value);
    setError("");
  };

  const onCloseHandler = () => setIsOpen(false);

  const isSubmitBtnDisabled =
    !feedback ||
    !rating ||
    isPending ||
    !!error ||
    isWhiteSpacesOnly;

  const isSuccess = showSuccessMsg && isFeedbackSuccess;

  return (
    <div className={styles["submit-feedback-modal"]}>
      <Modal
        open={isOpen}
        closeOnClickOutside={false}
        headerIcon={isSuccess ? undefined : CommentsQuestionCheck}
        title={isSuccess ? "" : t('innerLayout.feedbackForm.header')}
        hasCloseIcon={true}
        onCloseHandler={() => onCloseHandler()}
        btnsProps={
          isSuccess ?
            undefined
            : [
              {
                size: "lg",
                text: t('innerLayout.feedbackForm.submitButton'),
                disabled: isSubmitBtnDisabled,
                onClickHandler: () => mutateFeedback(),
                loading: isPending,
              }
            ]}
      >
        {
          isSuccess ?
            <StatusIndicator
              status="success"
              icon={Check}
              text={t('innerLayout.feedbackForm.success')}
              hintText={t('innerLayout.feedbackForm.successHint')}
            />
            :
            <div className={styles["submit-feedback-modal__body"]}>

              <Rating
                onRatingChange={(rating) => setRating(rating)}
              />

              <TextArea
                name="submit-feedback-textarea"
                lang={lang}
                variant="default"
                disabled={isPending}
                required
                autoFocus
                value={feedback}
                placeholder={t('innerLayout.feedbackForm.placeholder')}
                helperText={t('innerLayout.feedbackForm.helperText')}
                errorMessage={error}
                showCharactersCount
                onChange={onChangeHandler}
              />

            </div>
        }

      </Modal>
    </div>
  )
}
